import React, { useEffect, useMemo, useState } from 'react'
import { CircleFlag } from 'react-circle-flags'
import { Location, LocationSearchResult } from '@/lib/definitions/api.types'
import { useSearchLocations } from '@/lib/queries/Services.queries'
import LoadingSpinner from '@/components/LoadingSpinner'
import { SelectWithSearch } from '@/components/SelectInputs'
import { cn } from '@/lib/misc/Utils'

const isExtension = (locationResult: LocationSearchResult): boolean =>
  locationResult.location.type === 'Extension' ||
  !!locationResult.location.supplier_name

const LocationOptionLabel: React.FC<{ location: LocationSearchResult }> = ({
  location,
}) => (
  <div className="flex items-start w-full">
    <div className="w-4 h-4 shrink-0 mr-3">
      <CircleFlag
        countryCode={location.location.city.country.short_name.toLowerCase()}
        height="16"
        className="rounded-full border border-hairline"
      />
    </div>
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex items-center space-x-2">
        <span className="font-semibold text-neutral-90">
          {location.location.name}
        </span>
        <span className="text-neutral-50">{location.location.city.name}</span>
        {isExtension(location) && (
          <span className="shrink-0 rounded-full bg-neutral-10 px-2 py-0.5 text-sm font-medium text-neutral-50">
            Extension
          </span>
        )}
        {location.location.status !== 'Active' && (
          <span className="shrink-0 rounded-full bg-neutral-10 px-2 py-0.5 text-sm font-medium text-neutral-50">
            {COMING_SOON_GROUP}
          </span>
        )}
      </div>
      <div className="text-neutral-50 space-x-2">
        <span className="text-neutral-50">{location.location.description}</span>
        {location.location.line1 && (
          <>
            <span>-</span>
            <span className="text-sm">
              {location.location.line1}, {location.location.postal_code}{' '}
            </span>
          </>
        )}
      </div>
    </div>
  </div>
)

const COMING_SOON_GROUP = 'Coming soon'

const isOrderable = (locationResult: LocationSearchResult): boolean =>
  locationResult.location.status === 'Active'

const hasOrderableLocation = (locations: LocationSearchResult[]): boolean =>
  locations.some(isOrderable)

const CountryOptionLabel: React.FC<{
  country: {
    name: string
    shortName: string
    locations: LocationSearchResult[]
  }
  className?: string
}> = ({ country, className }) => (
  <div className={cn('flex items-center w-full', className)}>
    <div className="w-4 h-4 shrink-0 mr-3">
      <CircleFlag countryCode={country.shortName} height="16" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center">
        <span className="font-medium text-neutral-90 truncate">
          {country.name}
        </span>
        <span className="text-neutral-50 shrink-0 ml-2 bg-neutral-10 rounded-full px-2 py-0.5 text-sm font-medium group-hover/option:bg-neutral-05">
          {country.locations.length} location
          {country.locations.length !== 1 ? 's' : ''}
        </span>
        {!hasOrderableLocation(country.locations) && (
          <span className="text-neutral-50 shrink-0 ml-2 bg-neutral-10 rounded-full px-2 py-0.5 text-sm font-medium">
            {COMING_SOON_GROUP}
          </span>
        )}
      </div>
    </div>
  </div>
)

const locationSearchText = (locationResult: LocationSearchResult): string =>
  [
    locationResult.location.name,
    locationResult.location.city.name,
    locationResult.location.city.country.name,
    locationResult.location.description,
    locationResult.location.line1,
    locationResult.location.postal_code,
    isExtension(locationResult) ? 'Extension' : '',
  ]
    .filter(Boolean)
    .join(' ')

interface ScrollableCountryLocationsSelectorProps {
  selectedLocation?: number | string
  onLocationSelect: (location: Location | undefined) => void
  onCountrySelect?: (countryName: string | null) => void
  title?: string
  productPID?: string
  isOverGRE?: boolean
  isCountryDropdownOpen?: boolean
  className?: string
  includeExtensions?: boolean
}

const ScrollableCountryLocationsSelector: React.FC<
  ScrollableCountryLocationsSelectorProps
> = ({
  selectedLocation,
  onLocationSelect,
  onCountrySelect,
  title = 'Find your desired location',
  productPID,
  isOverGRE = false,
  isCountryDropdownOpen: initialCountryDropdownOpen = false,
  className,
  includeExtensions,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false)
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(
    initialCountryDropdownOpen
  )

  const { data: locationSearchResults, isLoading } = useSearchLocations({
    productPID: productPID,
    allowsGRETunnels: isOverGRE,
    includeExtensions,
  })

  // Group locations by country
  const countriesData = useMemo(() => {
    if (!locationSearchResults?.results) return []

    const countriesMap = new Map<
      string,
      { name: string; shortName: string; locations: LocationSearchResult[] }
    >()

    locationSearchResults.results.forEach((locationResult) => {
      const countryName = locationResult.location.city.country.name
      const countryShortName =
        locationResult.location.city.country.short_name.toLowerCase()

      if (!countriesMap.has(countryName)) {
        countriesMap.set(countryName, {
          name: countryName,
          shortName: countryShortName,
          locations: [],
        })
      }

      countriesMap.get(countryName)!.locations.push(locationResult)
    })

    countriesMap.forEach((country) =>
      country.locations.sort(
        (a, b) =>
          Number(!isOrderable(a)) - Number(!isOrderable(b)) ||
          Number(isExtension(a)) - Number(isExtension(b))
      )
    )

    // Sort countries alphabetically, with the not-yet-orderable ones last
    return Array.from(countriesMap.values()).sort(
      (a, b) =>
        Number(!hasOrderableLocation(a.locations)) -
          Number(!hasOrderableLocation(b.locations)) ||
        a.name.localeCompare(b.name)
    )
  }, [locationSearchResults])

  // Set selected country based on selected location when component mounts or location changes
  useEffect(() => {
    if (selectedLocation && countriesData.length > 0 && !selectedCountry) {
      const locationData = countriesData
        .flatMap((country) => country.locations)
        .find(
          (locationResult) => locationResult.location.id === selectedLocation
        )

      if (locationData) {
        const countryName = countriesData.find((country) =>
          country.locations.some((loc) => loc.location.id === selectedLocation)
        )?.name

        if (countryName) {
          setSelectedCountry(countryName)
        }
      }
    }
  }, [selectedLocation, countriesData, selectedCountry])

  // Clear selected location if it's not in the currently selected country
  useEffect(() => {
    if (selectedCountry && selectedLocation) {
      const selectedCountryData = countriesData.find(
        (country) => country.name === selectedCountry
      )
      const locationExistsInCountry = selectedCountryData?.locations.some(
        (locationResult) => locationResult.location.id === selectedLocation
      )

      if (!locationExistsInCountry) {
        onLocationSelect(undefined)
      }
    }
  }, [selectedCountry, selectedLocation, countriesData, onLocationSelect])

  const handleCountryClick = (countryName: string) => {
    const newSelectedCountry = countryName
    setSelectedCountry(newSelectedCountry)
    setIsCountryDropdownOpen(false)
    onCountrySelect?.(newSelectedCountry)

    // If selecting a new country and it has only one location, auto-select it
    if (newSelectedCountry && newSelectedCountry !== selectedCountry) {
      const selectedCountryData = countriesData.find(
        (country) => country.name === newSelectedCountry
      )
      if (selectedCountryData && selectedCountryData.locations.length === 1) {
        onLocationSelect(selectedCountryData.locations[0].location)
        setIsLocationDropdownOpen(false)
      } else if (
        selectedCountryData &&
        selectedCountryData.locations.length > 1
      ) {
        setIsLocationDropdownOpen(true)
      }
    }
  }

  const handleLocationClick = (location: Location | undefined) => {
    onLocationSelect(location)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner className="size-8" />
      </div>
    )
  }

  return (
    <div className={cn(className)}>
      {title && (
        <h1 className="text-2xl font-bold leading-[normal] tracking-[-0.24px] capitalize text-neutral-90 mb-1 md:mb-2 md:text-3xl md:normal-case">
          {title}
        </h1>
      )}

      <div className="space-y-4">
        {/* Country Selection */}
        <div>
          <h2 className="tracking-tightest text-neutral-50 mb-2">
            Select a Region
          </h2>

          <SelectWithSearch
            cy="select-country"
            label="Country"
            enableSearchField
            isOpen={isCountryDropdownOpen}
            value={
              selectedCountry ? (
                <CountryOptionLabel
                  country={
                    countriesData.find((c) => c.name === selectedCountry)!
                  }
                  className="pr-6"
                />
              ) : (
                ''
              )
            }
            options={countriesData.map((country) => ({
              value: country.name,
              label: country.name,
              formattedLabel: <CountryOptionLabel country={country} />,
              status: hasOrderableLocation(country.locations)
                ? 'Active'
                : 'Planned',
              disabled: !hasOrderableLocation(country.locations),
              group: hasOrderableLocation(country.locations)
                ? undefined
                : COMING_SOON_GROUP,
            }))}
            setValue={(value) => {
              if (typeof value === 'string') {
                handleCountryClick(value)
              }
            }}
            onReset={() => {
              setSelectedCountry(null)
              onLocationSelect(undefined)
              onCountrySelect?.(null)
              setIsLocationDropdownOpen(false)
              setIsCountryDropdownOpen(true)
            }}
            required
          />
        </div>

        {/* Location Selection within selected country */}
        <div className="mt-6">
          <div className="tracking-tightest text-neutral-50 mb-2">
            {selectedCountry
              ? `Select a Datacenter in ${selectedCountry}`
              : 'Select a Datacenter'}
          </div>
          {(() => {
            const countryLocations =
              countriesData.find((country) => country.name === selectedCountry)
                ?.locations || []
            const selectedLocationData = selectedLocation
              ? countryLocations.find(
                  (loc) => loc.location.id === selectedLocation
                )
              : null

            return (
              <SelectWithSearch
                cy="select-location"
                label="Location"
                enableSearchField
                disabled={!selectedCountry}
                isOpen={isLocationDropdownOpen}
                value={
                  selectedLocationData ? (
                    <LocationOptionLabel location={selectedLocationData} />
                  ) : (
                    ''
                  )
                }
                options={countryLocations.map((locationResult) => ({
                  value: locationResult.location.id.toString(),
                  label: locationSearchText(locationResult),
                  formattedLabel: (
                    <LocationOptionLabel location={locationResult} />
                  ),
                  status: locationResult.location.status,
                  disabled: !isOrderable(locationResult),
                  group: isOrderable(locationResult)
                    ? undefined
                    : COMING_SOON_GROUP,
                }))}
                setValue={(value) => {
                  if (
                    value &&
                    typeof value === 'string' &&
                    !isNaN(Number(value))
                  ) {
                    const selectedLocationData = countryLocations.find(
                      (locationResult) =>
                        locationResult.location.id.toString() === value
                    )
                    if (selectedLocationData) {
                      handleLocationClick(selectedLocationData.location)
                      setIsLocationDropdownOpen(false)
                    }
                  }
                }}
                onReset={() => {
                  if (selectedLocationData) {
                    handleLocationClick(undefined)
                    setIsLocationDropdownOpen(false)
                  }
                }}
                required
              />
            )
          })()}
        </div>
      </div>
    </div>
  )
}

export default ScrollableCountryLocationsSelector
